import { useCallback, useLayoutEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { cn } from '../../utils/cn';
import { MaintenanceControl } from './MaintenanceControl';
import { ComingSoonBanner, Countdown } from './ComingSoon';
import type { MaintenanceOptions } from '../../services/maintenanceApi';
import { sharedReleaseAt } from '../../utils/comingSoon';
import { StatusLink, configListPathFor, configPathFor } from './StatusLink';
import { compareNames, finiteMetric, groupServerRows, hostKey, reportAge, serviceState } from '../../utils/statusMonitorTable';
import type { ClusterOrder, HostRow, ServerRow } from '../../utils/statusMonitorTable';
import './statusOverview.css';
import { CduAgentStatus } from './CduAgentStatus';

export const metricNumber = (value: unknown, digits = 0) => {
  const metric = finiteMetric(value);
  return metric === null ? '—' : metric.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

// A coming-soon server's tick is staff testing behind a whitelist, not players
// suffering, so its numbers are shown without alarm colours.
export const performanceTone = (row: ServerRow) => !row.online || row.comingSoon ? 'muted' : row.stats.tps < 10 || row.stats.mspt > 100 ? 'danger' : row.performance ? 'warning' : 'good';

export function MetricBar({ value, max = 100, secondary, tone = 'good' }: { value: number | null; max?: number; secondary?: number | null; tone?: string }) {
  const width = (n: number) => `${Math.max(0, Math.min(100, n / max * 100))}%`;
  return <div className={`monitor-bar monitor-tone-${tone}`} aria-hidden="true">
    {secondary != null && Number.isFinite(secondary) && <span className="monitor-bar-cache" style={{ width: width(secondary) }} />}
    {value !== null && Number.isFinite(value) && <span style={{ width: width(value) }} />}
  </div>;
}

function Metric({ label, children, tone }: { label: string; children: ReactNode; tone?: string }) {
  return <div className="monitor-metric"><dt>{label}</dt><dd className={tone ? `monitor-tone-${tone}` : undefined}>{children}</dd></div>;
}

// Maintenance outranks health in the tone, because a server that is down on
// purpose is not an incident and must not read like one. Coming soon outranks
// both: it is news, and gets the teaser colour rather than a warning one.
export const serverTone = (row: ServerRow) => row.comingSoon ? 'soon' : row.maintenance ? 'warning'
  : row.health === 'offline' || row.critical ? 'danger' : row.health === 'attention' ? 'warning' : 'good';

export function ServerSignals({ row, now }: { row: ServerRow; now: number }) {
  const { listing } = row;
  // A server that has not opened yet has no outage to describe. What a reader
  // wants is when it opens and whether it is up for staff in the meantime.
  if (row.comingSoon) {
    return <div className="monitor-signals monitor-tone-soon">
      <span className="monitor-maintenance-word">Coming soon</span>
      {row.releaseAt !== null ? <span><Countdown releaseAt={row.releaseAt} /></span> : <span>No release time</span>}
      <span className="monitor-tone-muted">{row.online || listing?.online ? 'Running · not open to the public' : 'Not running yet'}</span>
    </div>;
  }
  const seen = Number.isFinite(row.stats.last_seen) && row.stats.last_seen > 0 ? ` · last seen ${reportAge(row.stats.last_seen, now)}` : '';
  return <div className={cn('monitor-signals', `monitor-tone-${serverTone(row)}`)}>
    {row.maintenance && <span className="monitor-maintenance-word">In maintenance</span>}
    {!row.online ? <>
      <span>{row.reported ? row.extendedDowntime ? 'Extended outage' : 'No heartbeat' : listing?.online ? 'Not reporting' : 'Offline in server list'}{seen}</span>
      {listing && (listing.online ? <span>Listed online</span> : row.reported ? <span>Offline in server list</span> : null)}
    </> : row.reasons.length ? row.reasons.map(reason => <span key={reason}>{reason}</span>) : <span>Online</span>}
  </div>;
}

export function PopularityScores({ row, period }: { row: ServerRow; period?: number }) {
  const values = [row.stats.popularity, row.stats.popularity_7d, row.stats.popularity_28d].map(score => {
    const value = finiteMetric(score?.popularity_index);
    return value !== null && value >= 0 ? value : null;
  });
  return <div className="monitor-popularity" aria-label="Popularity scores">
    {values.map((value, i) => {
      if (period !== undefined && period !== i) return null;
      const previous = values[i + 1];
      const delta = value !== null && previous != null ? Math.round(value) - Math.round(previous) : null;
      const label = ['24h', '7d', '28d'][i];
      return <span key={label} className="monitor-score"><span className={period !== undefined ? 'sr-only' : undefined}>{label}</span> <b>{metricNumber(value)}</b>
        {delta !== null && <span className={`monitor-trend monitor-tone-${delta > 0 ? 'good' : delta < 0 ? 'warning' : 'muted'}`} title={`${label} ${delta > 0 ? 'above' : delta < 0 ? 'below' : 'equal to'} ${['7d', '28d'][i]} score`} aria-label={`${delta > 0 ? 'rising' : delta < 0 ? 'falling' : 'unchanged'} versus ${['7d', '28d'][i]}`}>{delta > 0 ? '↑' : delta < 0 ? '↓' : '='}</span>}
      </span>;
    })}
  </div>;
}

const regionClass = (row: ServerRow) => cn('monitor-region-cell',
  row.comingSoon ? 'monitor-region-soon' : row.maintenance ? 'monitor-region-maintenance'
    : `monitor-region-${row.health === 'offline' ? 'offline' : row.critical ? 'critical' : row.health === 'attention' ? 'problem' : 'online'}`);
/** One server in a tooltip. A coming-soon server says so first, not its missing heartbeat. */
const regionSummary = (row: ServerRow) => row.comingSoon
  ? `Coming soon${row.releaseAt ? ` · opens ${new Date(row.releaseAt * 1000).toLocaleString()}` : ''}`
  : row.reasons.join(', ') || 'Healthy';
const clusterAnchor = (id: string) => `monitor-cluster-${encodeURIComponent(id)}`;

export function FleetStrip({ rows, clusterOrder, onCluster, selected }: { rows: ServerRow[]; clusterOrder: ClusterOrder; onCluster: (id: string) => void; selected: string | null }) {
  const groups = groupServerRows(rows, clusterOrder);
  return <div className="monitor-fleet-strip" aria-label="Every server at a glance">
    {groups.map(group => <button type="button" className="monitor-fleet-cluster" key={group.id} aria-pressed={selected === group.id} onClick={() => onCluster(group.id)}
      aria-label={`${group.name}: ${group.rows.length} ${group.rows.length === 1 ? 'server' : 'servers'}, ${group.offlineCount} offline, ${group.attentionCount} need attention${group.comingSoonCount ? `, ${group.comingSoonCount} coming soon` : ''}`} title={`${group.name}\n${group.rows.map(row => `${row.stats.serverId}: ${regionSummary(row)}`).join('\n')}`}>
      <span>{group.id || 'Unassigned'}</span>
      <span className="monitor-fleet-regions">{[...group.rows].sort((a, b) => compareNames(a.stats.serverId, b.stats.serverId)).map(row => <span key={row.id} className={`monitor-fleet-region monitor-tone-${serverTone(row)}`} title={`${row.stats.serverId}: ${regionSummary(row)}`}><span className="monitor-health-dot" />{row.stats.serverId}</span>)}</span>
    </button>)}
  </div>;
}

/** How long a tile takes to slide to a new place. Long enough to follow, short enough to ignore. */
const TILE_MOVE_MS = 420;
const TILE_EASING = 'cubic-bezier(0.22, 0.61, 0.36, 1)';

interface TilePlace { top: number; left: number }

// Content-sized rows pack small clusters beneath larger ones without changing
// the DOM's priority order or leaving column-sized gaps between panels.
//
// Three things make that honest rather than approximate.
//
// The spans are measured before the browser paints, so a tile that has just
// appeared or just changed height is never shown at the span it was last
// given. Measuring after paint is what left card-sized holes at the foot of
// the grid whenever the layout shifted: a tile that shrank kept reserving the
// rows it used to need, and a tile that had only just arrived reserved the
// stylesheet's guess.
//
// The re-measure a ResizeObserver asks for is deferred to the next frame
// rather than written from inside its own callback. A browser answers a
// resize loop by dropping notifications, and the round it drops is the one
// that would have corrected the span.
//
// And a tile that ends up somewhere else is played from where it was, so a
// re-order reads as movement rather than as the page having been replaced
// between two blinks.
function OverviewGrid({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  // Where each tile sat last time, so a move can be played backwards.
  const places = useRef(new Map<string, TilePlace>());
  const settled = useRef(false);
  const queued = useRef(0);

  const settle = useCallback((animate: boolean) => {
    const element = ref.current;
    if (!element) return;
    const items = Array.from(element.children) as HTMLElement[];
    const still = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    // Height first: a tile cannot be told where it landed until every tile
    // above it has claimed the rows it actually needs. Every height is read
    // before any span is written, so this costs the browser one layout rather
    // than one per tile.
    const rows = Number.parseFloat(getComputedStyle(element).gridAutoRows) || 4;
    // The item is measured, not the card inside it: `align-items: start` sizes
    // it to its own content, so its height is the height the grid has to find
    // room for, the spacing below it included.
    const heights = items.map(item => item.getBoundingClientRect().height);
    items.forEach((item, index) => {
      const height = `span ${Math.max(1, Math.ceil(heights[index] / rows))}`;
      if (item.style.gridRowEnd !== height) item.style.gridRowEnd = height;
    });

    const previous = places.current;
    const current = new Map<string, TilePlace>();
    const moved: { item: HTMLElement; x: number; y: number }[] = [];
    // offsetTop/offsetLeft are layout, so they read the same whether or not a
    // transform from the last move is still playing out on this tile.
    const found = items.map(item => ({ top: item.offsetTop, left: item.offsetLeft }));
    items.forEach((item, index) => {
      const place = found[index];
      const id = item.dataset.tile || String(index);
      current.set(id, place);
      const was = previous.get(id);
      if (!animate || still) return;
      if (!was) item.classList.add('monitor-tile-entering');
      else if (was.top !== place.top || was.left !== place.left) {
        // A tile still arriving would otherwise keep its entrance keyframes,
        // and an animation outranks the inline transform a move is made of.
        item.classList.remove('monitor-tile-entering');
        moved.push({ item, x: was.left - place.left, y: was.top - place.top });
      }
    });
    places.current = current;
    if (!moved.length) return;

    for (const { item, x, y } of moved) {
      item.style.transition = 'none';
      item.style.transform = `translate(${x}px, ${y}px)`;
    }
    // Reading layout back flushes the offset position into the browser's idea
    // of "before", which is what makes the line below a transition instead of
    // two writes it is free to collapse into one.
    void element.offsetHeight;
    for (const { item } of moved) {
      item.style.transition = `transform ${TILE_MOVE_MS}ms ${TILE_EASING}`;
      item.style.transform = '';
    }
  }, []);

  // Every render: re-span, then play whatever moved. The first pass has no
  // "before" to play from, so it only measures.
  useLayoutEffect(() => {
    settle(settled.current);
    settled.current = true;
  });

  // Height changes React did not cause: a diagnostics panel opening, a font
  // arriving, the column count changing under a window resize.
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const sizes = new ResizeObserver(() => {
      cancelAnimationFrame(queued.current);
      queued.current = requestAnimationFrame(() => settle(true));
    });
    const watch = () => {
      sizes.observe(element);
      for (const item of element.children) sizes.observe(item);
    };
    watch();
    const membership = new MutationObserver(watch);
    membership.observe(element, { childList: true });
    const entered = (event: AnimationEvent) => (event.target as HTMLElement).classList?.remove('monitor-tile-entering');
    element.addEventListener('animationend', entered);
    return () => {
      cancelAnimationFrame(queued.current);
      element.removeEventListener('animationend', entered);
      membership.disconnect();
      sizes.disconnect();
    };
  }, [settle]);

  return <div className="monitor-overview-grid monitor-packed-grid" ref={ref}>{children}</div>;
}

function ClusterReadings({ rows, now, onHost, configLinks }: { rows: ServerRow[]; now: number; onHost: (host: string) => void; configLinks?: boolean }) {
  const live = (row: ServerRow, n: unknown, digits = 0) => row.online ? metricNumber(n, digits) : '—';
  const software = (row: ServerRow) => row.reported
    ? `${row.stats.modloader || 'Unknown loader'} ${row.stats.modloaderVersion || ''} · MC ${row.stats.mcVersion || 'Unknown'}`
    : `MC ${row.stats.mcVersion || 'Unknown'} · from the server list`;
  const players = (row: ServerRow) => row.online
    ? <>{metricNumber(row.stats.playerCount)}<span className="monitor-capacity"> / {metricNumber(row.stats.maxPlayers)}</span></>
    : row.listing?.online
      ? <span title="Player count from the server list">{metricNumber(row.listing.players)}<span className="monitor-capacity"> / {metricNumber(row.listing.maxPlayers)}</span></span>
      : '—';
  // A listed server the feed has never seen has no loader to compare; it must not split the software line.
  const known = rows.filter(row => row.reported);
  const shown = known.length ? known : rows;
  const sameSoftware = shown.every(row => software(row) === software(shown[0]));
  const reading = (label: ReactNode, render: (row: ServerRow) => ReactNode, className?: string) => <tr className={className}><th scope="row">{label}</th>{rows.map(row => <td key={row.id} className={regionClass(row)}>{render(row)}</td>)}</tr>;
  return <>
    <table className={cn('monitor-matrix', rows.length > 2 && 'monitor-matrix-many')}>
      <caption className="sr-only">Regional server readings</caption>
      <thead><tr><td />{rows.map(row => <th key={row.id} className={regionClass(row)} scope="col" aria-label={row.name}>
        <span className="monitor-region-title"><span className={`monitor-health-dot monitor-tone-${serverTone(row)}`} title={regionSummary(row)} /><span>{row.stats.serverId || row.stats.identifier}</span></span>
        {row.host ? <button type="button" className="status-host-link" onClick={() => onHost(row.host)}>{row.host}</button> : <span className="monitor-host-missing">{row.reported ? 'Unknown host' : 'Not reporting'}</span>}
        {configLinks && row.address && <StatusLink to={configPathFor(row.address)} className="status-host-link monitor-config-link" title={`Open the config for ${row.address}`}>config</StatusLink>}
      </th>)}</tr></thead>
      <tbody>
        {reading('TPS', row => <span className={`monitor-tone-${performanceTone(row)}`}>{live(row, row.stats.tps, 1)}</span>, 'monitor-matrix-tps')}
        {reading('MSPT', row => <span className={row.online && !row.comingSoon && row.stats.mspt > 50 ? `monitor-tone-${row.stats.mspt > 100 ? 'danger' : 'warning'}` : undefined}>{live(row, row.stats.mspt, 1)}</span>)}
        {reading('Players', players, 'monitor-matrix-players monitor-matrix-group-end')}
        {reading('Backup', row => <span className={row.backup.severity === 'critical' ? 'monitor-tone-danger' : row.backup.severity === 'concern' ? 'monitor-tone-warning' : row.backup.severity === 'ok' ? 'monitor-tone-good' : undefined} title={row.backup.age !== null ? new Date(row.stats.last_backup_taken! * 1000).toLocaleString() : 'No backup timestamp reported'}>{row.backup.label}</span>, 'monitor-matrix-divider monitor-matrix-supporting')}
        {reading('Quasar', row => <span className={row.outdated ? 'monitor-tone-warning' : undefined}>{row.stats.quasarVersion || 'Unknown'}</span>, 'monitor-matrix-supporting')}
        {reading('CDU Agent', row => <CduAgentStatus hostname={row.host} compact />, 'monitor-matrix-supporting')}
        {reading('Last seen', row => <span className={row.health === 'offline' ? 'monitor-tone-danger' : !row.comingSoon && (!row.online || row.delayed) ? 'monitor-tone-warning' : 'monitor-tone-muted'}>{reportAge(row.stats.last_seen, now)}</span>, 'monitor-matrix-seen monitor-matrix-group-end')}
        {reading(<span title="Popularity over the last 24 hours" aria-label="Popularity, 24h">24h pop.</span>, row => <PopularityScores row={row} period={0} />, 'monitor-matrix-divider monitor-popularity-row')}
        {reading(<span title="Popularity over the last 7 days" aria-label="Popularity, 7d">7d pop.</span>, row => <PopularityScores row={row} period={1} />, 'monitor-popularity-row')}
        {reading(<span title="Popularity over the last 28 days" aria-label="Popularity, 28d">28d pop.</span>, row => <PopularityScores row={row} period={2} />, 'monitor-popularity-row monitor-popularity-last monitor-matrix-group-end')}
        {!sameSoftware && reading('Software', row => row.reported ? software(row) : '—', 'monitor-matrix-divider monitor-matrix-software')}
      </tbody>
    </table>
    {sameSoftware && (known.length > 0 || shown[0].stats.mcVersion) && <div className="monitor-software">{software(shown[0])}</div>}
  </>;
}

function ClusterDiagnostics({ rows, label }: { rows: ServerRow[]; label: string }) {
  const ordered = [...rows].sort((a, b) => compareNames(a.stats.serverId, b.stats.serverId));
  return <details className="monitor-diagnostics">
    <summary aria-label={`${label} diagnostics`}>Diagnostics</summary>
    {Array.from({ length: Math.ceil(ordered.length / 4) }, (_, index) => {
      const regionRows = ordered.slice(index * 4, index * 4 + 4);
      return <table key={index} className={cn('monitor-matrix monitor-diagnostic-readings', regionRows.length > 2 && 'monitor-matrix-many')}>
        <caption className="sr-only">{label} diagnostics</caption>
        <thead><tr><td />{regionRows.map(row => <th scope="col" key={row.id} className={regionClass(row)}>{row.stats.serverId || row.stats.identifier}</th>)}</tr></thead>
        <tbody>{[
          { label: 'Entities', value: (row: ServerRow) => metricNumber(row.stats.entities) },
          { label: 'Chunks', value: (row: ServerRow) => metricNumber(row.stats.chunks) },
          { label: 'Redis ping', value: (row: ServerRow) => `${metricNumber(row.stats.redisPing)} ms` },
        ].map(metric => <tr key={metric.label}><th scope="row">{metric.label}</th>{regionRows.map(row => <td key={row.id} className={regionClass(row)}>{row.online ? metric.value(row) : '—'}</td>)}</tr>)}</tbody>
      </table>;
    })}
  </details>;
}

export function MinecraftStatusOverview({ rows, now, onHost, groupByCluster, clusterOrder, onMaintenance, configLinks }: {
  rows: ServerRow[]; now: number; onHost: (host: string) => void; groupByCluster: boolean; clusterOrder: ClusterOrder;
  /** Absent for viewers without the permission, which is what hides the control. */
  onMaintenance?: (addresses: string[], maintenance: boolean, options?: MaintenanceOptions) => Promise<void>;
  /** Link each cluster and server to its config, for viewers who can read configs. */
  configLinks?: boolean;
}) {
  const groups = groupByCluster ? groupServerRows(rows, clusterOrder) : rows.map(row => ({ id: row.id, name: row.stats.modPackName || row.stats.clusterId, rows: [row], offlineCount: row.health === 'offline' ? 1 : 0, attentionCount: row.health === 'attention' ? 1 : 0, maintenanceCount: row.maintenance && !row.comingSoon ? 1 : 0, comingSoonCount: row.comingSoon ? 1 : 0 }));
  return <OverviewGrid>{groups.map(group => {
    const label = groupByCluster ? group.id || 'Unassigned' : group.rows[0].stats.identifier;
    const allInMaintenance = group.maintenanceCount === group.rows.length;
    const allComingSoon = group.comingSoonCount === group.rows.length;
    const soonRows = group.rows.filter(row => row.comingSoon);
    // Health is reported over the servers that are open; a server that has
    // not opened yet is neither healthy nor a problem.
    const open = group.rows.filter(row => !row.comingSoon);
    const critical = open.filter(row => row.critical).length;
    return <div key={group.id} data-tile={group.id || 'unassigned'}><section id={clusterAnchor(group.id)} tabIndex={-1} className={cn('glass-card monitor-cluster', group.maintenanceCount > 0 && 'monitor-cluster-maintaining', allInMaintenance && 'monitor-cluster-maintenance', allComingSoon && 'monitor-cluster-soon')} aria-label={group.name}>
      <header className="monitor-cluster-heading">
        <div><h2>{label}</h2><p>{group.name}</p></div>
        <div className="monitor-cluster-actions">
          {/* Said once. With the control present its own label carries the window,
              so the status line keeps reporting health; without it (no permission)
              this is the only place a reader gets the word. Coming soon has its
              own banner below, so it is never repeated here. */}
          {group.maintenanceCount > 0 && !onMaintenance
            ? <span className="monitor-cluster-status monitor-tone-warning">{allInMaintenance ? 'In maintenance' : `${group.maintenanceCount} in maintenance`}</span>
            : open.length > 0 && <span className={cn('monitor-cluster-status', group.offlineCount > 0 || critical > 0 ? 'monitor-tone-danger' : group.attentionCount > 0 ? 'monitor-tone-warning' : 'monitor-tone-good')}>{group.offlineCount > 0 ? `${group.offlineCount} offline` : critical > 0 ? `${critical} critical` : group.attentionCount > 0 ? `${group.attentionCount} ${group.attentionCount === 1 ? 'needs' : 'need'} attention` : `${open.length} healthy`}</span>}
          {configLinks && group.rows[0]?.stats.clusterId && <StatusLink to={configListPathFor(group.rows[0].stats.clusterId)} className="status-host-link monitor-config-link" title={`Open the configs for ${group.rows[0].stats.clusterId}`}>Configs</StatusLink>}
          {onMaintenance && <MaintenanceControl target={{ label, rows: group.rows }} onToggle={onMaintenance} />}
        </div>
      </header>
      {/* A partial cluster says how much of it is new, so "coming soon" is
          never read as the state of the servers that are already open. */}
      {soonRows.length > 0 && <ComingSoonBanner
        releaseAt={sharedReleaseAt(soonRows)}
        scope={allComingSoon ? undefined : `${soonRows.length} of ${group.rows.length} servers`} />}
      {Array.from({ length: Math.ceil(group.rows.length / 4) }, (_, index) => <ClusterReadings key={index} rows={[...group.rows].sort((a, b) => compareNames(a.stats.serverId, b.stats.serverId)).slice(index * 4, index * 4 + 4)} now={now} onHost={onHost} configLinks={configLinks} />)}
      {group.rows.some(row => row.reasons.length > 0 || row.maintenance) && <div className="monitor-cluster-alerts">{group.rows.filter(row => row.reasons.length > 0 || row.maintenance).map(row => <div key={row.id} className={`monitor-tone-${serverTone(row)}`}><strong>{row.stats.serverId}</strong><ServerSignals row={row} now={now} /></div>)}</div>}
      <ClusterDiagnostics rows={group.rows} label={label} />
    </section></div>;
  })}</OverviewGrid>;
}

export function HostReadings({ row }: { row: HostRow }) {
  const { stats, online, memory } = row;
  const value = (raw: string) => online ? metricNumber(Number.parseFloat(raw), 1) : '—';
  return <div className="monitor-host-readings">
    <div>
      <dl><Metric label="CPU" tone={row.cpu !== null && row.cpu > 90 ? 'danger' : undefined}>{online ? `${metricNumber(row.cpu, 1)}%` : '—'}</Metric></dl>
      <MetricBar value={online ? row.cpu : null} tone={row.cpu !== null && row.cpu > 90 ? 'danger' : 'accent'} />
      <dl><Metric label="Memory" tone={memory.effectivePercent > 90 ? 'danger' : undefined}>{online && Number(memory.total) > 0 ? `${memory.used} / ${memory.total} GB` : '—'}</Metric></dl>
      <MetricBar value={online ? memory.effectivePercent : null} secondary={online ? memory.htopPercent : null} tone={memory.effectivePercent > 90 ? 'danger' : 'accent'} />
      <p className="monitor-memory-caption">{online && Number(memory.total) > 0 ? `${memory.percentage}% in use${memory.htopPercent !== null ? ` · ${memory.htopPercent.toFixed(1)}% incl. cache` : ''}` : 'Memory reading unavailable'}</p>
      <dl className="monitor-network">
        <Metric label="Upload">{value(stats.bandwidth_up)} Mbps</Metric>
        <Metric label="Download">{value(stats.bandwidth_down)} Mbps</Metric>
        <Metric label="Cloudflare">{value(stats.ping_cloudflare)} ms</Metric>
        <Metric label="Google">{value(stats.ping_google)} ms</Metric>
      </dl>
    </div>
    <div>
      <h3 className="monitor-section-label">Storage</h3>
      {row.disks.length ? row.disks.map(disk => <div className="monitor-disk" key={disk.device}>
        <div><span title={disk.device}>{disk.device.replace(/^\/dev\//, '')}</span><b>{online ? `${disk.used} / ${disk.size} · ${metricNumber(Number.parseFloat(disk.usage_percent))}%` : '—'}</b></div>
        <MetricBar value={online ? finiteMetric(Number.parseFloat(disk.usage_percent)) : null} tone={Number.parseFloat(disk.usage_percent) > 85 ? 'danger' : Number.parseFloat(disk.usage_percent) > 70 ? 'warning' : 'good'} />
      </div>) : <p className="monitor-tone-muted">No disks ≥ 10 GB reported</p>}
      <h3 className="monitor-section-label">Services <span>{online && row.services.length ? `${row.services.filter(service => serviceState(service.status) === 'open').length}/${row.services.length} open` : 'No current result'}</span></h3>
      <div className="monitor-services">{row.services.map(service => <span key={`${service.identifier}-${service.port}`} className={`monitor-tone-${!online || serviceState(service.status) === 'unknown' ? 'muted' : serviceState(service.status) === 'open' ? 'good' : 'danger'}`} title={`Port ${service.port}`}>
        {service.identifier} <b>{online ? service.status || 'Unknown' : 'Unknown'}</b>
      </span>)}</div>
    </div>
  </div>;
}

export function HostStatusOverview({ rows, now, onHost }: { rows: HostRow[]; now: number; onHost: (host: string) => void }) {
  return <div className="monitor-overview-grid monitor-host-grid">{rows.map(row => <article key={row.name} className="glass-card monitor-cluster monitor-host" aria-label={hostKey(row.name)}>
    <header className="monitor-cluster-heading"><div><h2><span className={`monitor-health-dot monitor-tone-${!row.online ? 'danger' : row.health === 'attention' ? 'warning' : 'good'}`} />{hostKey(row.name)}</h2><button type="button" className="status-host-link" onClick={() => onHost(hostKey(row.name))}>{row.instances.length} Minecraft servers</button></div><span className="monitor-tone-muted">Seen {reportAge(row.stats.last_seen, now)}</span></header>
    <HostReadings row={row} />
    <div className="monitor-host-agent"><CduAgentStatus hostname={row.name} /></div>
    {row.reasons.length > 0 && <div className={`monitor-cluster-alerts monitor-tone-${!row.online ? 'danger' : 'warning'}`}>{row.reasons.join(' · ')}</div>}
  </article>)}</div>;
}
